import { Box, Input, TextProps, Text } from '@betty/moonlight';
import { memo, useEffect, useRef, useState } from 'react';
import {
  NameErrorValues,
  NameInputProps,
  NameInputValues,
} from './NameInput.types';
import { updateNameFieldValue, validateField } from './NameInput.utils';
import { useSelector } from '@betty/toolkit/state';
import { NAME_FIELDS } from './NameInput.constants';
import { selectAccountName } from '../../../../state';
import { TextInput } from 'react-native';

function NameInput({ setIsNameValid, onLastFieldSubmit }: NameInputProps) {
  const inputRefs = useRef<(TextInput | null)[]>([]);

  const userNameValues = useSelector(selectAccountName);

  const [updatedValues, setUpdatedValues] = useState<NameInputValues | null>(
    null,
  );

  const [errors, setErrors] = useState<NameErrorValues>({});

  useEffect(() => {
    const isAllValid = Object.entries(userNameValues).every(([key, value]) => {
      const error = errors[key as keyof NameInputValues];

      if (value === '') return error === undefined;
      return error === null;
    });

    setIsNameValid(isAllValid);
  }, [errors, userNameValues, setIsNameValid]);

  const handleChange = (field: keyof NameInputValues) => (value: string) => {
    setUpdatedValues((prev) => updateNameFieldValue(prev, field, value));
    setErrors((prev) => validateField(prev, value, field, userNameValues));
  };

  const handleFocus = (field: keyof NameInputValues) => () => {
    if (updatedValues?.[field] !== undefined) return;

    const value = '';
    setUpdatedValues((prev) => updateNameFieldValue(prev, field, value));
    setErrors((prev) => validateField(prev, value, field, userNameValues));
  };

  const handleSubmitEditing = (index: number) => {
    const fields = Object.keys(userNameValues);
    if (index < fields.length - 1) {
      inputRefs.current[index + 1]?.focus();
    } else {
      onLastFieldSubmit();
    }
  };

  return (
    <Box gap="space-200" marginTop="space-400">
      {NAME_FIELDS.map((field, index) => (
        <Box key={field} gap="space-200" marginBottom="space-300">
          <Input
            ref={(el) => (inputRefs.current[index] = el)}
            value={updatedValues?.[field] ?? ''}
            placeholderNS="responsibleGaming"
            placeholderKey={field as TextProps['locKey']}
            onChangeText={handleChange(field)}
            onFocus={handleFocus(field)}
            enterKeyHint="next"
            onSubmitEditing={() => handleSubmitEditing(index)}
            error={errors[field] ?? undefined}
            icon={errors[field] === null ? 'check' : undefined}
          />

          {errors[field] ? (
            <Text
              variant="caption"
              locNS="responsibleGaming"
              locKey={errors[field] as TextProps['locKey']}
              color="darkSurfaceError"
            />
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

export default memo(NameInput);
